import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@/utils/cn';
import { pairComplete, type PairDraft } from '../builder/draft';

interface PairTabsProps {
  pairs: PairDraft[];
  activePairId: string;
  onSelect: (pairId: string) => void;
}

/**
 * Which pair the prescription, frame and lens steps are working on.
 *
 * "Pair 1" is a label for this screen only: the server numbers the jobs when the order is confirmed. Each pair says in words whether it is ready, not only with an icon's colour.
 */
export function PairTabs({ pairs, activePairId, onSelect }: PairTabsProps) {
  const { t } = useTranslation();
  if (pairs.length < 2) return null;

  return (
    <nav aria-label={t('opticalOrders.pairs.label')}>
      <ul className="flex flex-wrap gap-2">
        {pairs.map((pair, index) => {
          const isActive = pair.id === activePairId;
          const ready = pairComplete(pair);
          const Icon = ready ? CheckCircle2 : Circle;
          return (
            <li key={pair.id}>
              <button
                type="button"
                aria-pressed={isActive}
                onClick={() => onSelect(pair.id)}
                className={cn(
                  'inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm',
                  'focus-visible:ring-brand-200 focus-visible:ring-2 focus-visible:outline-none',
                  isActive
                    ? 'border-brand-600 bg-brand-50 text-brand-800 font-medium'
                    : 'border-line text-ink bg-white',
                )}
              >
                <Icon
                  className={cn('size-4', ready ? 'text-emerald-600' : 'text-muted')}
                  aria-hidden
                />
                {t('opticalOrders.pairs.pair', { number: index + 1 })}
                {pair.usage ? (
                  <span className="text-muted">
                    {' · '}
                    {t(`opticalOrders.usage.pairLabel.${pair.usage}`)}
                  </span>
                ) : null}
                <span className="sr-only">
                  {` (${ready ? t('opticalOrders.pairs.complete') : t('opticalOrders.pairs.incomplete')})`}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
